/**
 * Query keys for the supplier portal, namespaced per SCOPE.
 *
 * A supplier's board and the Habits team's view of the same board are
 * different payloads (the internal one carries our status and internal notes),
 * so they never share a cache entry. Within a scope, keys run general →
 * specific so a write can invalidate exactly the columns it touched.
 */
import type { BoardWindow } from "@/modules/SupplierOrders";

import type { PortalBoardFilters, PortalScope } from "../types.ts";

export function scopeKey(scope: PortalScope): string {
  return scope.kind === "supplier" ? "supplier" : `internal:${scope.supplierId}`;
}

export const portalKeys = {
  all: ["supplier-portal"] as const,
  session: ["supplier-portal", "session"] as const,
  scope: (scope: PortalScope) => ["supplier-portal", scopeKey(scope)] as const,
  board: (scope: PortalScope) => ["supplier-portal", scopeKey(scope), "board"] as const,
  /** The board page's one-call first load; its answer is copied into the slice keys below. */
  bootstrap: (scope: PortalScope, filters: PortalBoardFilters, pageSize: number) =>
    ["supplier-portal", scopeKey(scope), "bootstrap", filters, pageSize] as const,
  /** Prefixes for every window's summary / methods / counts, for invalidation. */
  summaries: (scope: PortalScope) => ["supplier-portal", scopeKey(scope), "summary"] as const,
  summary: (scope: PortalScope, window: BoardWindow) =>
    ["supplier-portal", scopeKey(scope), "summary", window] as const,
  methodsAll: (scope: PortalScope) => ["supplier-portal", scopeKey(scope), "methods"] as const,
  methods: (scope: PortalScope, window: BoardWindow) =>
    ["supplier-portal", scopeKey(scope), "methods", window] as const,
  countsAll: (scope: PortalScope) => ["supplier-portal", scopeKey(scope), "counts"] as const,
  counts: (scope: PortalScope, window: BoardWindow) =>
    ["supplier-portal", scopeKey(scope), "counts", window] as const,
  /** Prefix for every column page in the scope. */
  columns: (scope: PortalScope) => ["supplier-portal", scopeKey(scope), "column"] as const,
  column: (scope: PortalScope, columnId: string, filters: PortalBoardFilters, pageSize: number) =>
    ["supplier-portal", scopeKey(scope), "column", columnId, filters, pageSize] as const,
  schedules: (scope: PortalScope) => ["supplier-portal", scopeKey(scope), "schedule"] as const,
  schedule: (scope: PortalScope, from: string, to: string, filters: PortalBoardFilters) =>
    ["supplier-portal", scopeKey(scope), "schedule", from, to, filters] as const,
  orders: (scope: PortalScope) => ["supplier-portal", scopeKey(scope), "order"] as const,
  order: (scope: PortalScope, id: string) => ["supplier-portal", scopeKey(scope), "order", id] as const,
  comments: (scope: PortalScope, id: string) => ["supplier-portal", scopeKey(scope), "comments", id] as const,
  history: (scope: PortalScope, id: string) => ["supplier-portal", scopeKey(scope), "history", id] as const,
  codes: (scope: PortalScope, query: string) => ["supplier-portal", scopeKey(scope), "codes", query] as const,
};

/** The column id inside a `portalKeys.column` key. */
export function columnIdOfKey(key: readonly unknown[]): string | null {
  return key[2] === "column" && typeof key[3] === "string" ? key[3] : null;
}

/**
 * Every cache a Work on change patches optimistically: the board columns, the
 * Schedule ranges, and each changed order's page. The mutation snapshots all
 * of these, so a refused write can put every one of them back.
 */
export function workOnPatchedKeys(scope: PortalScope, supplierOrderIds: readonly string[]): (readonly unknown[])[] {
  return [
    portalKeys.columns(scope),
    portalKeys.schedules(scope),
    ...supplierOrderIds.map((id) => portalKeys.order(scope, id)),
  ];
}
